import {fadeIn, fadeOut} from "./utilities/fade";
import {changeRequiredAttributes} from "./order";
import {focusInputGroups} from "./checkInputGroups";
import {getScrollbarWidth} from "./utilities/functions";
import {addTableWrapper} from "./tableWrapper";
import {initSlidecontents} from "./slidecontent";
import EventHandler from "./dom/event-handler";
import {setAccessibilityLiveRegionMessage} from "./accessibilityLiveRegionService";
import {initRecaptchaForms} from "./recaptchaForm";

function dispatchCustomModalContentLoadedEvent(data,name="modalContentLoaded"){
    document.dispatchEvent(new CustomEvent(name, {
        detail: { data }
    }));
}

function showModalBackground() {

    if (document.querySelector('.modalBg') == null) {
        const modalbg = document.createElement('div');
        modalbg.classList.add('modalBg');
        document.body.appendChild(modalbg);
        document.body.classList.add('modal-open');

        if(window.outerWidth > GLOBALS.gridBreakpoints.sm) {
            document.body.style.paddingRight = getScrollbarWidth() + "px";
        }

        fadeIn(modalbg, 400);
        modalbg.addEventListener('click', function () {
            closeModal();
        });
    }
}

function removeModalBackground() {
    if (!document.querySelectorAll('.modal.open').length) {
        for (const modalbg of document.querySelectorAll('.modalBg')) {
            fadeOut(modalbg, 400, "linear", function () {
                modalbg.remove();
                document.body.classList.remove('modal-open');
                document.body.style.paddingRight = "";
            });
        }
    }
}

function resetModalStyles(modal) {
    modal.style.zIndex = "";
}

export function generateNewModal(templateID) {
    const template = document.getElementById(templateID);
    if (template) {
        const newModal = document.body.appendChild(template.content.cloneNode(true).querySelector('.modal'));
        newModal.setAttribute("id", "newModal");
        return newModal;
    } else {
        console.error("Cannot find Modal Template");

        return false;
    }
}

function disablePrevOpenedModals(modals,currentModal){
    for(const modal of modals){
        if(modal !== currentModal){
            modal.classList.add('modal--disabled');
        }
    }
}

function getAllOpenModals(){
    return document.querySelectorAll('.modal.open');
}

function enableLastOpenedModal(){
    const allOpenedModals = getAllOpenModals();
    if(allOpenedModals){
        const index = allOpenedModals.length - 1;
        if(index >= 0){
            for(const modal of allOpenedModals){
                if(parseInt(modal.dataset.index) === index){
                    modal.classList.remove("modal--disabled");
                }
            }
        }else{
            document.removeEventListener("keydown", eventEscapeCloseModal);
        }
    }
}

let lastFocusedElement;

function eventEscapeCloseModal(event) {
    if(event.key === "Escape"){
        const allOpenedModals = document.querySelectorAll('.modal.open');
        if (allOpenedModals.length > 0) {
            closeModal(allOpenedModals[allOpenedModals.length - 1]);
        }
    }
}

function handleTabKey(event) {
    if (event.key !== 'Tab') return;

    const modal = document.querySelector('.modal.open:not(.modal--disabled)');
    if (!modal) return;

    const focusableElementsSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), audio[controls], video[controls], details, summary, .modal__title, .modal__body';
    const allFocusableElements = Array.from(modal.querySelectorAll(focusableElementsSelector))
        .filter(el => {
            return !el.hasAttribute('disabled') &&
                el.getAttribute('aria-hidden') !== 'true' &&
                (el.tagName === 'DIV' || el.tagName === 'H1' || el.tagName === 'H2' || el.tagName === 'H3' || el.tagName === 'H4' || el.tagName === 'H5' || el.tagName === 'H6' || el.type !== 'hidden') &&
                (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0);
        });

    if (allFocusableElements.length === 0) {
        event.preventDefault();
        return;
    }

    const firstFocusableElement = allFocusableElements[0];
    const lastFocusableElement = allFocusableElements[allFocusableElements.length - 1];

    if (event.shiftKey) { // Shift + Tab
        if (document.activeElement === firstFocusableElement || document.activeElement === modal) {
            lastFocusableElement.focus();
            event.preventDefault();
        }
    } else { // Tab
        if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            event.preventDefault();
        }
    }
}

export function closeModal(modal = null) {
    let modals = [];
    if (modal === null) {
        modals = document.querySelectorAll('.modal.open');
    } else {
        modals.push(modal);
    }

    if (modals) {
        for (const currentModal of modals) {
            resetModalStyles(currentModal);
            currentModal.classList.remove('open');
            currentModal.classList.remove('show');
            const frames = currentModal.querySelectorAll('iframe,video');
            for (const frame of frames) {
                const src = frame.getAttribute('src');
                if (src) {
                    frame.setAttribute('src', '');
                    frame.setAttribute('src', src);
                }
            }
            enableLastOpenedModal();

            const noModalsLeft = getAllOpenModals().length === 0;

            if (noModalsLeft) {
                document.removeEventListener("keydown", handleTabKey);
            }

            setTimeout(function () {
                currentModal.style.display = "";
                removeModalBackground();

                // Focus management after modal is fully hidden
                if (noModalsLeft) {
                    if (lastFocusedElement) {
                        lastFocusedElement.focus();
                        lastFocusedElement = null;
                    } else if (document.querySelector("#container > main")) {
                        const focusable = document.querySelector("#container > main").querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                        if (focusable) {
                            focusable.focus();
                        } else {
                            document.body.focus();
                        }
                    }
                } else {
                    // Stacked modal: focus the topmost remaining open modal
                    const remainingModals = getAllOpenModals();
                    const topModal = remainingModals[remainingModals.length - 1];
                    if (topModal) {
                        const focusTarget = topModal.querySelector('.modal__title, .modal__body, .modal__close');
                        if (focusTarget) focusTarget.focus();
                    }
                }
            }, 200);

            EventHandler.trigger(currentModal, 'hidden.modal');
        }
    }

    setAccessibilityLiveRegionMessage(GLOBALS.tc.modalClosed);

}

export function showModal(modal, callback = () => {}, options = {}) {
    if (modal) {
        const allOpenedModals = document.querySelectorAll('.modal.open');
        const index = allOpenedModals.length;

        // Store current focused element as trigger
        if (index === 0) {
            lastFocusedElement = document.activeElement;
        }


        const defaultOptions = {
            type: "modal",
            direction: ""
        };

        options = {...defaultOptions, ...options};

        if (options.type === 'flyout') {
            modal.classList.add('modal--flyout');
            if (options.direction === 'left') {
                modal.classList.add(`modal--flyoutLeft`);
            } else {
                modal.classList.add(`modal--flyoutRight`);
            }
        }

        modal.setAttribute('data-index', index);

        modal.style.display = "flex";
        showModalBackground();

        setTimeout(function () {
            disablePrevOpenedModals(allOpenedModals,modal);
            modal.classList.add('open');
            if (index > 0) {
                modal.style.zIndex = parseInt(getComputedStyle(modal).zIndex + index);
            }
            setTimeout(function (){
                changeRequiredAttributes(modal.querySelectorAll('.isRequiredVisible'));
            },200);
            focusInputGroups(modal.querySelectorAll('.inputGroup input,.inputGroup select,.inputGroup textarea'));

            // Accessibility: Focus title, body or close button
            const modalTitle = modal.querySelector(".modal__title");
            const modalBody = modal.querySelector(".modal__body");
            const modalClose = modal.querySelector(".modal__close");
            
            if (modalTitle) {
                modalTitle.setAttribute("tabindex", "-1");
                modalTitle.focus();
            } else if (modalBody) {
                modalBody.setAttribute("tabindex", "-1");
                modalBody.focus();
            } else if (modalClose) {
                modalClose.focus();
            }
            setAccessibilityLiveRegionMessage(GLOBALS.tc.modalOpened);
            dispatchCustomModalContentLoadedEvent({
                target: modal
            });
            callback();
        }, 200);

        modal.addEventListener('mouseup', function (e) {
            if (e.target.closest(".modal__dialog") === null) {
                closeModal(modal);
            }
        });

        for(const closeTarget of modal.querySelectorAll("[data-close='modal']")){
            closeTarget.addEventListener('click', function () {
                closeModal(modal);
            });
            closeTarget.addEventListener('keyup', function (e) {
                if(e.keyCode === 13){
                    closeModal(modal);
                }
            });
        }

        if(index === 0){
            document.addEventListener("keyup", eventEscapeCloseModal);
            document.addEventListener("keydown", handleTabKey);
        }
    }
}

export function triggerModal(query = document.querySelectorAll('[data-modal]')) {

    for (const modalTrigger of query) {
        modalTrigger.addEventListener('click', function (e) {
            showModal(document.getElementById(modalTrigger.dataset.modal));
            e.preventDefault();
        });
    }

    for (const openModalOnLoad of document.querySelectorAll('.modal--openOnLoad')) {
        showModal(openModalOnLoad);
    }
}

export function excludeModal(query = document.querySelectorAll('.modal:not(.modal-stay)')) {
    for (const modal of query) {
        document.body.append(modal);
    }
}

export function triggerOpenLinkInModal(cssselector = 'js-openLinkInModal') {
    document.addEventListener('click', function (e) {
        let target = false;
        if(e.target.classList.contains(cssselector)){
            target = e.target;
        }
        if(e.target.closest("a") && e.target.closest("a").classList.contains(cssselector)){
            target = e.target.closest("a");
        }
        if (target) {
            const modalTrigger = target;
            const href = modalTrigger.href;
            const title = modalTrigger.title;
            if (href) {
                const modal = generateNewModal("modalTemplate");
                if (modal) {
                    const modalClass = modalTrigger.dataset.modalClass;
                    if(modalClass){
                        modal.classList.add(modalClass);
                    }
                    showModal(modal);
                    modalTrigger.ariaBusy="true";
                    fetch(modalTrigger.href)
                        .then((response) => {
                            if (!response.ok) {
                                throw "Could not load content";
                            }
                            return response.text();
                        })
                        .then((data) => {
                            const doc = new DOMParser().parseFromString(data, "text/html");
                            const contents = doc.querySelectorAll(".loadContentInModal");
                            const modalBody = modal.querySelector('.modal__body');
                            const modalTitle = modal.querySelector('.modal__title');
                            if (modalTitle && title) {
                                modalTitle.innerHTML = title;
                            }
                            if (contents && modalBody) {
                                modal.classList.remove("modal--loading");
                                modalBody.innerHTML = "";
                                for (const content of contents) {
                                    modalBody.innerHTML = modalBody.innerHTML + content.innerHTML;
                                }

                                focusInputGroups(modalBody.querySelectorAll('.inputGroup input,.inputGroup select,.inputGroup textarea'));
                                addTableWrapper(modalBody.querySelectorAll('.textcontent table, .modal__body table, .slidecontent table'));
                                initSlidecontents(modalBody.querySelectorAll('.slidecontentItem__headline'));
                                initRecaptchaForms(modalBody);

                                dispatchCustomModalContentLoadedEvent({
                                    target: modal
                                });

                                modalTrigger.ariaBusy="false";
                            } else {
                                window.location.href = href;
                            }
                        })
                        .catch((e) => {
                            modalTrigger.ariaBusy="false";
                            console.error('Error:', e);
                        });
                    e.preventDefault();
                }
            }
        }
    });
}

export function triggerConfirmModalLink(cssselector = 'js-confirmModalLink') {
    document.addEventListener('click', function (e) {
        let target = false;
        if(e.target.classList.contains(cssselector)){
            target = e.target;
        }
        if(e.target.closest("a") && e.target.closest("a").classList.contains(cssselector)){
            target = e.target.closest("a");
        }
        if (target) {
            const modalTrigger = target;
            const href = modalTrigger.href;
            const text = modalTrigger.dataset.text;
            const confirmBtnText = modalTrigger.dataset.confirmBtn;
            const confirmBtnClass = modalTrigger.dataset.confirmBtnClass;
            if (href) {
                const modal = generateNewModal("confirmModalTemplate");
                if (modal) {
                    const modalBody = modal.querySelector('.modal__body');
                    const modalConfirmBtn = modal.querySelector('.js-confirmModalButton');
                    if (modalBody && text) {
                        modalBody.innerHTML = text;
                    }
                    if (modalConfirmBtn && confirmBtnText) {
                        modalConfirmBtn.innerText = confirmBtnText;
                        if (confirmBtnClass) {
                            modalConfirmBtn.classList.remove("button--primary");
                            modalConfirmBtn.classList.add(confirmBtnClass);
                        }

                        modalConfirmBtn.addEventListener("click", function () {
                            console.log(href);
                            window.location.href = href;
                        });
                    }
                    showModal(modal);
                    e.preventDefault();
                }
            }
        }
    });
}

export function triggerOpenTextInModal(cssselector = 'js-openTextInModal') {
    document.addEventListener('click', function (e) {
        let target = false;
        if(e.target.classList.contains(cssselector)){
            target = e.target;
        }
        if(e.target.closest("a") && e.target.closest("a").classList.contains(cssselector)){
            target = e.target.closest("a");
        }
        if (target) {
            const modalTrigger = target;
            let text = "no text";
            let title = "";

            if(modalTrigger.dataset.text){
                text = modalTrigger.dataset.text;
            }

            if(modalTrigger.dataset.headline){
                title = modalTrigger.dataset.headline;
            }
            const modal = generateNewModal("modalTemplate");
            if (modal) {
                modal.classList.remove("modal--loading");
                const modalClass = modalTrigger.dataset.modalClass;
                if(modalClass){
                    modal.classList.add(modalClass);
                }
                const modalBody = modal.querySelector('.modal__body');
                const modalTitle = modal.querySelector('.modal__title');
                if (modalTitle) {
                    modalTitle.innerHTML = title;
                }
                if (modalBody) {
                    modalBody.innerHTML = "<div>"+text+"</div>";
                }
                showModal(modal);
                e.preventDefault();
            }
        }
    });
}
